/**
 * Сервис уведомлений о синхронизации
 * Показывает временные индикаторы и уведомления о результатах синхронизации
 */

import { notification } from 'antd';
import { forceUpdateAmoFieldsValidation } from '../utils/amoFieldsUtils';

/**
 * Показать уведомление об успешной синхронизации
 * @param {Object} customField - Кастомное поле
 * @param {*} value - Синхронизированное значение
 * @param {Object} amoField - AMO поле
 * @param {boolean} wasAutoReplaced - Была ли автоматическая замена
 */
export function showSyncSuccessNotification(customField, value, amoField, wasAutoReplaced = false) {
    const message = wasAutoReplaced 
        ? `Автоматическая замена в "${amoField.name}"`
        : `Синхронизация "${customField.name}"`;
    
    const description = Array.isArray(value) 
        ? `Значения: ${value.join(', ')}`
        : `Значение: ${value}`;
    
    notification.success({
        message,
        description,
        duration: 3,
        placement: 'topRight'
    });
}

/**
 * Показать предупреждение о конфликте
 * @param {Object} customField - Кастомное поле
 * @param {*} value - Значение
 * @param {Object} conflict - Информация о конфликте
 */
export function showSyncConflictNotification(customField, value, conflict) {
    notification.warning({
        message: `Конфликт: "${customField.name}"`,
        description: `${conflict.reason || 'AMO поле уже заполнено другим значением'}`,
        duration: 5,
        placement: 'topRight'
    });
}

/**
 * Показать ошибку синхронизации
 * @param {Object} customField - Кастомное поле
 * @param {*} value - Значение
 * @param {string} error - Текст ошибки
 */
export function showSyncErrorNotification(customField, value, error) {
    notification.error({
        message: `Ошибка синхронизации "${customField.name}"`,
        description: error,
        duration: 5,
        placement: 'topRight'
    });
}

/**
 * Создать временный индикатор синхронизации для поля
 * @param {number} fieldId - ID кастомного поля
 * @param {string} status - Статус ('syncing', 'success', 'error', 'conflict')
 * @param {number} duration - Длительность показа в миллисекундах
 * @returns {Function} Функция для удаления индикатора
 */
export function showFieldSyncIndicator(fieldId, status = 'syncing', duration = 2000) {
    
    const fieldContainer = document.querySelector(`[data-field-id="${fieldId}"]`) ||
                          document.querySelector(`[data-id="${fieldId}"]`) ||
                          document.querySelector(`.field-${fieldId}`);
    
    if (!fieldContainer) {
        console.warn(`Контейнер поля ${fieldId} не найден для индикатора`);
        return () => {};
    }
    
    
    const indicator = document.createElement('div');
    indicator.className = `sync-indicator sync-indicator--${status}`;
    indicator.style.cssText = `
        position: absolute;
        top: -8px;
        right: -8px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        z-index: 1000;
        animation: syncPulse 1s infinite;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    `;
    
    
    const colors = {
        syncing: '#1890ff',   
        success: '#52c41a',   
        error: '#ff4d4f',     
        conflict: '#faad14'   
    };
    
    indicator.style.backgroundColor = colors[status] || colors.syncing;
    
    
    if (!document.querySelector('#sync-indicator-styles')) {
        const styles = document.createElement('style');
        styles.id = 'sync-indicator-styles';
        styles.textContent = `
            @keyframes syncPulse {
                0% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.2); opacity: 0.7; }
                100% { transform: scale(1); opacity: 1; }
            }
            .sync-indicator--success {
                animation: syncSuccess 0.5s ease-out;
            }
            @keyframes syncSuccess {
                0% { transform: scale(1); }
                50% { transform: scale(1.5); }
                100% { transform: scale(1); }
            }
        `;
        document.head.appendChild(styles);
    }
    
    
    if (getComputedStyle(fieldContainer).position === 'static') {
        fieldContainer.style.position = 'relative';
    }
    
    fieldContainer.appendChild(indicator);
    
    
    const removeIndicator = () => {
        if (indicator && indicator.parentNode) {
            indicator.remove();
        }
    };
    
    
    if (duration > 0) {
        setTimeout(removeIndicator, duration);
    }
    
    return removeIndicator;
}

/**
 * Показать индикатор только для успешной синхронизации
 * @param {Object} params - Параметры уведомления
 */
export function showSyncFeedback({ 
    customField, 
    value, 
    status, 
    message, 
    amoField = null, 
    conflict = null,
    wasAutoReplaced = false 
}) {
    
    if (status === 'success') {
        console.log(`✅ Синхронизация ${customField.name}: ${value}`, {
            amoField: amoField?.name,
            wasAutoReplaced
        });
    } else {
        console.log(`Синхронизация ${customField.name} пропущена:`, {
            status,
            reason: message || conflict?.reason
        });
    } 
}

/**
 * Показать индикатор процесса синхронизации
 * @param {number} fieldId - ID поля
 * @returns {Function} Функция для завершения индикации
 */
export function startSyncIndicator(fieldId) {
    
    return () => {}; 
} 